<template>
  <div class="not-registered">
    <div class="register-to-vote__text-block register-to-vote__text-block--alert">
      <icon
        icon-name="alert-triangle"
        view-box="0 0 15 15"
        class="register-to-vote__icon--alert"
      />

      <p>
        {{ $t("cac.app.currently_not_registered_to_vote") }}
        {{ voterInformation.stateName }}
      </p>
      <p v-if="registrationMethodsPhrase">{{ registrationMethodsPhrase }}</p>
    </div>

    <a
      v-if="onlineRegistrationUrl"
      :href="onlineRegistrationUrl"
      class="register-to-vote__button register-to-vote__button--online-registration btn btn--primary"
      target="_blank"
    >{{ $t("cac.app.online_registration") }}</a>

    <a
      v-if="hasRegistrationForms"
      :class="paperRegistrationButtonClass"
      href="#"
      @click.prevent="togglePaperRegistrationExpanded"
    >
      {{ $t("cac.app.paper_registration") }}

      <icon
        :icon-name="iconNames.CHEVRON_DOWN"
        class="register-to-vote__icon register-to-vote__icon--chevron"
        view-box="0 0 32 32"
      />
    </a>

    <div
      v-if="hasRegistrationForms"
      :class="paperRegistrationClass"
    >
      <p>{{ $t("cac.app.download_and_print_follow_instructions") }}</p>

      <a
        v-if="hasEnglishRegistrationForm"
        :href="englishPaperRegistrationFormUrl"
        class="btn btn--secondary"
        target="_blank"
      >
        {{ $t("cac.app.download_english_form") }}
      </a>
      <a
        v-if="hasSpanishRegistrationForm"
        :href="spanishPaperRegistrationFormUrl"
        class="btn btn--secondary"
        target="_blank"
      >
        {{ $t("cac.app.download_spanish_form") }}
      </a>

      <div class="register-to-vote__text-block register-to-vote__text-block--help">
        <icon
          icon-name="help-circle"
          view-box="0 0 15 15"
          class="register-to-vote__icon--info"
        />

        <p v-if="onlineRegistrationUrl">
          {{ $t("cac.app.any_questions_check_your") }}
          <a
            :href="onlineRegistrationUrl"
            target="_blank"
          >{{ $t("cac.app.states_instructions_and_next_steps") }}</a>
        </p>
      </div>
    </div>

    <hr>

    <div class="register-to-vote__text-block register-to-vote__text-block--info">
      <icon
        :icon-name="iconNames.INFO"
        view-box="0 0 15 15"
        class="register-to-vote__icon--info"
      />

      <p>{{ $t("cac.app.thought_you_were_registered") }}</p>
    </div>

    <a
      :href="registrationUrl"
      class="register-to-vote__button register-to-vote__button--check-official-status btn btn--secondary"
      target="_blank"
    >{{ $t("cac.app.check_official_registration_status") }}</a>
    <a
      :href="resetCookieUrl"
      class="register-to-vote__link--re-enter-information"
    >{{ $t("cac.app.re_enter_information") }}</a>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import * as ICON_NAMES from "Constants/styleguide/iconNames";
import STATE from "Constants/campaign/civicactioncenter/state";
import Icon from "StyleguideComponents/Icon.vue";

const advocateRecordContext = `${STATE.SCOPE_FORMS}/${STATE.ADVOCATE_RECORD}`;
const basePaperRegistrationUrl = "cac-gotv/register-to-vote/paper";
const languageCodeEnglish = "en";
const languageCodeSpanish = "es";

export default {
  name: "NotRegistered",
  components: {
    Icon
  },
  props: {
    onlineRegistrationUrl: {
      type: String,
      default: ""
    },
    registrationUrl: {
      type: String,
      default: ""
    },
    resetCookieUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      iconNames: ICON_NAMES,
      paperRegistrationExpanded: false
    };
  },
  computed: {
    ...mapGetters({
      advocateRecord: advocateRecordContext
    }),
    ...mapState(STATE.SCOPE_CAC, {
      voterInformation: STATE.VOTER_INFORMATION
    }),
    englishPaperRegistrationFormUrl() {
      const { stateAbbr } = this.voterInformation.stateRegistrationInfo;

      return `${basePaperRegistrationUrl}/${stateAbbr.toUpperCase()}/${languageCodeEnglish}`;
    },
    hasRegistrationForms() {
      return this.hasEnglishRegistrationForm || this.hasSpanishRegistrationForm;
    },
    hasEnglishRegistrationForm() {
      return this.voterInformation.stateRegistrationInfo.paperRegistration.availableLanguages.includes(
        "en"
      );
    },
    hasSpanishRegistrationForm() {
      return this.voterInformation.stateRegistrationInfo.paperRegistration.availableLanguages.includes(
        "es"
      );
    },
    paperRegistrationClass() {
      return [
        "register-to-vote__forms",
        {
          "register-to-vote__forms--expanded": this.paperRegistrationExpanded
        }
      ];
    },
    paperRegistrationButtonClass() {
      return [
        "register-to-vote__button",
        "register-to-vote__button--paper-registration",
        "btn",
        "btn--primary",
        {
          "register-to-vote__button--expanded": this.paperRegistrationExpanded
        }
      ];
    },
    registrationMethodsPhrase() {
      if (this.onlineRegistrationUrl) {
        return this.$t("cac.app.you_can_register_online_or_mail");
      }

      if (this.hasRegistrationForms) {
        return this.$t("cac.app.you_can_register_mail");
      }

      return "";
    },
    spanishPaperRegistrationFormUrl() {
      const { stateAbbr } = this.voterInformation.stateRegistrationInfo;

      return `${basePaperRegistrationUrl}/${stateAbbr.toUpperCase()}/${languageCodeSpanish}`;
    }
  },
  methods: {
    togglePaperRegistrationExpanded() {
      this.paperRegistrationExpanded = !this.paperRegistrationExpanded;
    }
  }
};
</script>
